import { useCallback, useEffect, useRef, useState } from 'react'

export interface PaymentConfig {
  providers?: {
    yookassa?: boolean
    yandex_pay?: boolean
  }
  yandex_pay_split?: boolean
  cash_special?: boolean
  partial?: {
    enabled?: boolean
    min_rub?: number | null
    max_rub?: number | null
    presets?: Array<'10' | '25' | '30' | '50' | '75' | '100'>
  } | null
}

interface Stage {
  title?: string
  total_amount?: number | null
}

interface PublicPaymentWidgetProps {
  /** Kind used in the API path: 'mis-plans' | 'constructor-plans' */
  kind: 'mis-plans' | 'constructor-plans'
  /** Public share token */
  token: string
  /** Payment configuration block from the share snapshot */
  paymentConfig: PaymentConfig
  /** Total plan amount in RUB (for % presets) */
  totalAmountRub?: number | null
  /** Optional plan stages for per-stage presets */
  stages?: Stage[]
  /** Primary color for branding */
  accent?: string
  /** Return URL (passed to backend which builds the full return URL) */
  apiBase?: string
}

async function createPayment(
  apiBase: string,
  kind: 'mis-plans' | 'constructor-plans',
  token: string,
  body: {
    provider: string
    amount_kopecks: number
    yandex_split?: boolean
    description?: string
  },
): Promise<{ payment_url: string; id: number }> {
  const resp = await fetch(`${apiBase}/public/${kind}/${encodeURIComponent(token)}/payments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = (await resp.json()) as { success?: boolean; data?: { payment_url: string; id: number }; error?: string }
  if (!resp.ok || !json.success) {
    throw new Error(json.error ?? 'Ошибка создания платежа')
  }
  return json.data!
}

async function pollStatus(apiBase: string, linkId: number): Promise<string> {
  const resp = await fetch(`${apiBase}/public/payment-links/${linkId}/status`)
  const json = (await resp.json()) as { success?: boolean; data?: { status: string } }
  if (!resp.ok || !json.success) return 'unknown'
  return json.data?.status ?? 'unknown'
}

function formatRub(rub: number): string {
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(rub)
}

export function PublicPaymentWidget({
  kind,
  token,
  paymentConfig,
  totalAmountRub,
  stages = [],
  accent = '#2563eb',
  apiBase = '',
}: PublicPaymentWidgetProps) {
  const [amountRub, setAmountRub] = useState('')
  const [provider, setProvider] = useState<string>('')
  const [yandexSplit, setYandexSplit] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [successLinkId, setSuccessLinkId] = useState<number | null>(null)
  const [paidStatus, setPaidStatus] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const yookassaEnabled = !!(paymentConfig.providers?.yookassa)
  const yandexPayEnabled = !!(paymentConfig.providers?.yandex_pay)
  const splitEnabled = !!(paymentConfig.yandex_pay_split)
  const cashSpecial = !!(paymentConfig.cash_special)
  const partial = paymentConfig.partial

  // Set default provider
  useEffect(() => {
    if (provider === '') {
      if (yookassaEnabled) setProvider('yookassa')
      else if (yandexPayEnabled) setProvider('yandex_pay')
    }
  }, [yookassaEnabled, yandexPayEnabled, provider])

  // Check URL for ?pay=success and linkId
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search)
    if (qs.get('pay') === 'success') {
      const id = parseInt(qs.get('link_id') ?? '', 10)
      if (!isNaN(id)) {
        setSuccessLinkId(id)
      } else {
        setPaidStatus('success')
      }
    }
  }, [])

  // Poll status when we have a link id (after redirect from provider)
  useEffect(() => {
    if (!successLinkId) return
    let attempts = 0
    pollRef.current = setInterval(async () => {
      attempts++
      try {
        const status = await pollStatus(apiBase, successLinkId)
        if (status === 'paid') {
          setPaidStatus('paid')
          if (pollRef.current) clearInterval(pollRef.current)
        }
      } catch {
        // ignore
      }
      if (attempts >= 10) {
        if (pollRef.current) clearInterval(pollRef.current)
      }
    }, 2000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [successLinkId, apiBase])

  const presets: Array<{ label: string; rub: number }> = []

  if (totalAmountRub && partial?.enabled && partial.presets && partial.presets.length > 0) {
    for (const p of partial.presets) {
      const rub = Math.round(totalAmountRub * (parseInt(p) / 100))
      presets.push({ label: `${p}%`, rub })
    }
  }

  if (totalAmountRub) {
    if (!presets.find((p) => p.rub === totalAmountRub)) {
      presets.push({ label: '100%', rub: totalAmountRub })
    }
  }

  for (let i = 0; i < stages.length; i++) {
    const stage = stages[i]
    if (stage.total_amount && stage.total_amount > 0) {
      presets.push({ label: `Этап ${i + 1}${stage.title ? ` — ${stage.title}` : ''}`, rub: stage.total_amount })
    }
  }

  const parsedAmount = parseFloat(amountRub.replace(',', '.'))
  const amountKopecks = isNaN(parsedAmount) || parsedAmount <= 0 ? 0 : Math.round(parsedAmount * 100)

  const minRub = partial?.enabled && partial.min_rub ? partial.min_rub : null
  const maxRub = partial?.enabled && partial.max_rub ? partial.max_rub : totalAmountRub ?? null

  const amountError = amountKopecks > 0
    ? (minRub && parsedAmount < minRub ? `Минимум ${formatRub(minRub)}` : null) ??
      (maxRub && parsedAmount > maxRub ? `Максимум ${formatRub(maxRub)}` : null)
    : null

  async function handlePay(chosenProvider: string) {
    if (amountKopecks <= 0) {
      setError('Введите сумму оплаты.')
      return
    }
    if (amountError) {
      setError(amountError)
      return
    }
    setLoading(true)
    setError(null)
    setInfo(null)
    try {
      const result = await createPayment(apiBase, kind, token, {
        provider: chosenProvider,
        amount_kopecks: amountKopecks,
        yandex_split: chosenProvider === 'yandex_pay' ? yandexSplit : undefined,
        description: 'Оплата плана лечения',
      })
      window.open(result.payment_url, '_blank', 'noopener,noreferrer')
      setInfo('Форма оплаты открыта в новой вкладке. После оплаты вернитесь на эту страницу.')
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка при создании платежа')
      setInfo(null)
    } finally {
      setLoading(false)
    }
  }

  // ── Success state ─────────────────────────────────────────────────────────
  if (paidStatus === 'paid' || paidStatus === 'success') {
    return (
      <div
        style={{
          background: '#f0fdf4',
          border: '1px solid #86efac',
          borderRadius: 16,
          padding: '24px 28px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 40, marginBottom: 8 }}>✓</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#15803d', marginBottom: 4 }}>
          Оплата прошла успешно
        </div>
        <div style={{ fontSize: 14, color: '#166534' }}>
          Ваш платёж принят. Если у вас возникнут вопросы, обратитесь к администратору клиники.
        </div>
      </div>
    )
  }

  // ── Pending after redirect ────────────────────────────────────────────────
  if (successLinkId && !paidStatus) {
    return (
      <div
        style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 16,
          padding: '24px 28px',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 14, color: '#1e40af', fontWeight: 500 }}>
          Проверяем статус оплаты…
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 16,
        padding: '24px 28px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>Внести оплату</div>

      {/* Cash special warning */}
      {cashSpecial && (
        <div
          style={{
            background: '#fefce8',
            border: '1px solid #fde047',
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 13,
            color: '#854d0e',
          }}
        >
          Указаны спец. условия за наличные. Согласуйте оплату наличными с куратором
          перед использованием онлайн-оплаты.
        </div>
      )}

      {/* Amount input */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
          Сумма, ₽{maxRub ? ` (до ${formatRub(maxRub)})` : ''}
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="number"
            min={minRub ?? 1}
            max={maxRub ?? undefined}
            step={1}
            value={amountRub}
            onChange={(e) => setAmountRub(e.target.value)}
            placeholder={totalAmountRub ? String(Math.round(totalAmountRub)) : '0'}
            style={{
              flex: 1,
              padding: '10px 12px',
              border: `1px solid ${amountError ? '#fca5a5' : '#e5e7eb'}`,
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              outline: 'none',
            }}
          />
          {amountKopecks > 0 && (
            <span style={{ fontSize: 14, color: '#6b7280', whiteSpace: 'nowrap' }}>
              {formatRub(parsedAmount)}
            </span>
          )}
        </div>
        {amountError && <div style={{ fontSize: 12, color: '#ef4444' }}>{amountError}</div>}
      </div>

      {/* Presets */}
      {presets.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {presets.map((p, i) => {
            const belowMin = minRub !== null && p.rub < minRub
            return (
              <button
                key={i}
                type="button"
                disabled={belowMin}
                onClick={() => !belowMin && setAmountRub(String(p.rub))}
                title={belowMin ? `Меньше минимума (${formatRub(minRub!)})` : undefined}
                style={{
                  background: amountRub === String(p.rub) ? accent : '#f3f4f6',
                  color: amountRub === String(p.rub) ? '#fff' : belowMin ? '#aaa' : '#374151',
                  border: belowMin ? '1px dashed #ddd' : 'none',
                  borderRadius: 20,
                  padding: '5px 14px',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: belowMin ? 'not-allowed' : 'pointer',
                  opacity: belowMin ? 0.5 : 1,
                  transition: 'background 0.15s',
                  textDecoration: belowMin ? 'line-through' : 'none',
                }}
              >
                {p.label} — {formatRub(p.rub)}
              </button>
            )
          })}
        </div>
      )}

      {/* Yandex Split toggle */}
      {yandexPayEnabled && splitEnabled && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={yandexSplit}
            onChange={(e) => setYandexSplit(e.target.checked)}
          />
          Оплатить через Yandex Split (4 платежа без процентов)
        </label>
      )}

      {/* Info (new tab opened) */}
      {info && (
        <div style={{ fontSize: 13, color: '#1d4ed8', background: '#eff6ff', borderRadius: 8, padding: '8px 12px', border: '1px solid #bfdbfe' }}>
          {info}
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ fontSize: 13, color: '#ef4444', background: '#fef2f2', borderRadius: 8, padding: '8px 12px' }}>
          {error}
        </div>
      )}

      {/* Provider buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {yookassaEnabled && (
          <button
            type="button"
            disabled={loading || amountKopecks <= 0}
            onClick={() => void handlePay('yookassa')}
            style={{
              background: accent,
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '13px 20px',
              fontSize: 15,
              fontWeight: 600,
              cursor: loading || amountKopecks <= 0 ? 'not-allowed' : 'pointer',
              opacity: loading || amountKopecks <= 0 ? 0.6 : 1,
            }}
          >
            {loading ? 'Перенаправление...' : 'Оплатить через YooKassa'}
          </button>
        )}

        {yandexPayEnabled && (
          <div>
            <button
              type="button"
              disabled={loading || amountKopecks <= 0}
              onClick={() => void handlePay('yandex_pay')}
              style={{
                width: '100%',
                background: '#fc0',
                color: '#1a1a1a',
                border: 'none',
                borderRadius: 10,
                padding: '13px 20px',
                fontSize: 15,
                fontWeight: 700,
                cursor: loading || amountKopecks <= 0 ? 'not-allowed' : 'pointer',
                opacity: loading || amountKopecks <= 0 ? 0.6 : 1,
              }}
            >
              Оплатить через Yandex Pay
            </button>
            {splitEnabled && (
              <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280', textAlign: 'center' }}>
                Можно оплатить в Сплит — 4 платежа без переплат
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
